import React, { useState } from 'react';
import { formatDateRange } from '../../services/dateUtils';
import { BOOKING_STATUSES, USER_ROLES } from '../../constants/statuses';
import StatusBadge from '../common/StatusBadge';
import Button from '../common/Button';
import MessageModal from '../common/MessageModal';
import ReviewButton from '../reviews/ReviewButton';

const BookingDetails = ({
  booking,
  actionLoading,
  onEditDates,
  onCancel,
  onAccept,
  currentUserId,
  showCloseButton = false,
  onClose
}) => {
  const [showMessageModal, setShowMessageModal] = useState(false);

  const canEditDates = (booking) => {
    if (booking.current_user_role === USER_ROLES.HOST) {
      const allowedStatuses = [BOOKING_STATUSES.PENDING, BOOKING_STATUSES.ACCEPTED];

      // Check if host editing of in-progress bookings is enabled
      const allowHostEditInprogress = window.wwoofBooking?.settings?.allowHostEditInprogress || false;
      if (allowHostEditInprogress) {
        allowedStatuses.push(BOOKING_STATUSES.IN_PROGRESS);
      }

      return (
        allowedStatuses.includes(booking.status)
      );
    }

    return false;
  };

  const canCancel = (booking) => {
    const initiatedBy = booking.initiated_by || 'host';

    if (booking.current_user_role === USER_ROLES.HOST) {
      // Hosts can cancel pending or accepted bookings they initiated
      // For WWOOFer-initiated requests, they can also decline (cancel) when pending
      return [BOOKING_STATUSES.PENDING, BOOKING_STATUSES.ACCEPTED].includes(booking.status);
    } else if (booking.current_user_role === USER_ROLES.WWOOFER) {
      // WWOOFers can cancel pending bookings they initiated
      // For Host-initiated requests, they can decline (cancel) when pending
      return booking.status === BOOKING_STATUSES.PENDING;
    }
    return false;
  };

  const canAccept = (booking) => {
    if (booking.status !== BOOKING_STATUSES.PENDING) return false;

    const initiatedBy = booking.initiated_by || 'host';

    // WWOOFer-initiated: Host accepts
    if (initiatedBy === 'wwoofer') {
      return booking.current_user_role === USER_ROLES.HOST;
    }
    // Host-initiated: WWOOFer accepts
    return booking.current_user_role === USER_ROLES.WWOOFER;
  };

  const getCancelButtonLabel = (booking) => {
    const isPending = booking.status === BOOKING_STATUSES.PENDING;
    const initiatedBy = booking.initiated_by || 'host';
    
    if (isPending && initiatedBy === 'wwoofer') {
      if(booking.current_user_role === USER_ROLES.HOST)
        return 'Decline Request';
      else
        return 'Cancel Request';
    }

    if (isPending && initiatedBy === 'host') {
      if(booking.current_user_role === USER_ROLES.WWOOFER)
        return 'Decline Request';
      else
        return 'Cancel Request';
    } 
    return 'Cancel Booking';
    
  };

  return (
    <div className='booking-card'>
      <div className='booking-header'>
        <div>
          📅 {formatDateRange(booking.start_date, booking.end_date)}
        </div>
        <StatusBadge status={booking.status} initiatedBy={booking.initiated_by} />
      </div>

      <div className='booking-dates'>
        <p>
          With: {booking.current_user_role === USER_ROLES.HOST ? (
            <a
              href={booking.wwoofer_profile_url}
              target="_blank"
              rel="noopener noreferrer"
              className="profile-link"
            >
              {booking.wwoofer_name}
            </a>
          ) : (
            <a
              href={booking.host_profile_url}
              target="_blank"
              rel="noopener noreferrer"
              className="profile-link"
            >
              {booking.host_name}
            </a>
          )}
        </p>
      </div>

      <div className='booking-actions'>
        {canEditDates(booking) && (
          <Button
            variant='edit'
            disabled={actionLoading}
            onClick={() => onEditDates(booking)}
          >
            {actionLoading ? '...' : 'Edit Dates'}
          </Button>
        )}

        {canCancel(booking) && (
          <Button
            variant='cancel'
            disabled={actionLoading}
            onClick={() => onCancel(booking.id)}
          >
            {actionLoading ? '...' : getCancelButtonLabel(booking)}
          </Button>
        )}

        {canAccept(booking) && (
          <Button
            variant='accept'
            disabled={actionLoading}
            onClick={() => onAccept(booking)}
          >
            {actionLoading ? '...' : 'Accept'}
          </Button>
        )}

        <Button
          onClick={() => setShowMessageModal(true)}
          className="action-button"
        >
          💬 View Messages
        </Button>

        <ReviewButton
          booking={booking}
          currentUserId={currentUserId}
        />

        {showCloseButton && (
          <Button onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      {showMessageModal && (
        <MessageModal
          isOpen={showMessageModal}
          onClose={() => setShowMessageModal(false)}
          conversationId={booking.pm_thread_id}
          currentUserId={currentUserId}
          nonce={window.wwoofBooking?.nonce || window.wwoofBooking?.nonce || ''}
          onSendMessage={() => {
            // Optional callback for when a message is sent
            console.log('Message sent');
          }}
        />
      )}
    </div>
  );
};

export default BookingDetails;
